import useFreeShippingInformation from 'hooks/common/use-free-shipping-information';
import useFreeShippingThreshold from 'hooks/common/use-free-shipping-threshold';
import useMOVConditions from 'hooks/common/use-mov-conditions';
import { EffectiveCart } from 'interfaces/cart';
import { PriceSummaryConfig } from './types';

const FREE_SHIPPING_NOTICE_THRESHOLD = 500;

interface Args {
  config: Pick<PriceSummaryConfig, 'isExistingCustomer'> & Partial<PriceSummaryConfig>;
  effectiveCart?: EffectiveCart;
  isInCheckout?: boolean;
}

/**
 * Combines shipping and MOV-related hooks into the normalized config consumed by
 * the price summary components.
 *
 * The helper also decides whether the free-shipping notice should be shown,
 * taking customer status, current shipping state, and the thresholded cheapest
 * shipping method into account.
 */
export const usePriceSummaryConfig = ({
  config: configOverride,
  effectiveCart,
  isInCheckout = false,
}: Args): PriceSummaryConfig => {
  const { isNewCustomer } = useFreeShippingInformation();
  const { cheapestShippingMethod, insufficientMoneyFormatted, isShippingFree } =
    useFreeShippingThreshold({
      shippingMethods: effectiveCart?.shippingOptions.methods ?? [],
    });
  const { insufficientMoney, minimumBasketValue, shouldShowMinimumBasketNotice } = useMOVConditions(
    { effectiveCart, isInCheckout }
  );

  return {
    cheapestShippingMethod,
    insufficientMoney,
    insufficientMoneyFormatted,
    minimumBasketValue,
    selectedShipping: effectiveCart?.shipping,
    shouldShowFreeShippingNotice:
      !isNewCustomer &&
      !isShippingFree &&
      !!insufficientMoneyFormatted &&
      (!cheapestShippingMethod ||
        cheapestShippingMethod.reducedRequired < FREE_SHIPPING_NOTICE_THRESHOLD),
    shouldShowMinimumBasketNotice,
    ...configOverride,
  };
};
